import React, { useMemo, forwardRef } from 'react'
import { observer } from 'mobx-react'
import { runInAction } from 'mobx'
import { useTranslation } from 'react-i18next'
import {
  validateUsageRegionCountry,
  validateUsageRegionReceiver,
  validateUsageRegionTarget,
} from 'todistukset-common/src/vtj/form-validation.util'
import { isEidasIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import {
  getPropertyTranslationKey,
  getValidationErrorFromValidator,
} from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import getFormActions from 'todistukset-ui/src/vtj/stores/actions/get-form-actions'
import { setShowValidationErrors } from 'todistukset-ui/src/vtj/stores/actions/steps-actions'

import { CountryKoodistoCode } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import { UsageRegionReceiver } from 'todistukset-common/src/vtj/UsageRegionReceiver.enum'
import { UsageRegionTarget } from 'todistukset-common/src/vtj/UsageRegionTarget.enum'
import {
  isCountryInApostilleByCode,
  isCountryInEUByCode,
} from 'todistukset-common/src/vtj/country.util'

import {
  SingleSelect,
  type SingleSelectData,
  RadioButtonGroup,
  RadioButton,
  ExternalLink,
  Block,
  Heading,
  Paragraph,
  StatusText,
} from 'suomifi-ui-components'

import { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { useCountrySelectData } from 'todistukset-common/src/vtj/ui/country-select.util'
import { useAriaOptionsFunction } from 'todistukset-ui/src/vtj/components/order-wizard/country-select.util'
import { useFirstErrorImperativeHandle } from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'

import type { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  InfoInlineAlert,
  ParagraphWithNewlines,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import {
  FormContainer,
  SmallTextParagraph,
} from 'todistukset-ui/src/vtj/components/order-wizard/UsageRegionStep.sc'
import { isVtjOteTodistus } from 'todistukset-common/src/vtj/CertificateTypes.enum'

const UsageRegionStep: StepComponent = observer(
  forwardRef(function UsageRegionStep(
    { titleId, certificateType, orderFlowName },
    ref
  ) {
    if (!isVtjOteTodistus(certificateType)) {
      throw new Error(
        `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
      )
    }
    const { t } = useTranslation()
    const {
      forms: { [certificateType]: form },
      steps: {
        [certificateType]: { showStepValidationErrors },
      },
      common: { lang, loggedInUser },
    } = useStore()

    const {
      usageRegion: { target, countryKoodistoCode, receiver },
    } = form

    const isEidas = isEidasIdentifiedUser(loggedInUser.get())

    enum RegionSelection {
      FINLAND = `${UsageRegionTarget.TARGET_COUNTRY}_fi`,
      EU_AUTHORITY = UsageRegionTarget.EU_AUTHORITY,
      OTHER_COUNTRY = UsageRegionTarget.TARGET_COUNTRY,
    }

    const regionSelectItems: (SingleSelectData & {
      uniqueItemId: RegionSelection
      usageRegionTarget: UsageRegionTarget
      setCountry: CountryKoodistoCode | null
    })[] = useMemo(
      () =>
        [
          {
            labelText: t('orderWizardUsageRegionStepRegion_fi'),
            uniqueItemId: RegionSelection.FINLAND,
            usageRegionTarget: UsageRegionTarget.TARGET_COUNTRY,
            setCountry: EuCountries.FINLAND,
          },
          {
            labelText: t('orderWizardUsageRegionStepRegion_eu'),
            uniqueItemId: RegionSelection.EU_AUTHORITY,
            usageRegionTarget: UsageRegionTarget.EU_AUTHORITY,
            setCountry: null,
          },
          {
            labelText: t('orderWizardUsageRegionStepRegion_other'),
            uniqueItemId: RegionSelection.OTHER_COUNTRY,
            usageRegionTarget: UsageRegionTarget.TARGET_COUNTRY,
            setCountry: null,
          },
        ].filter(
          (item) =>
            !isEidas || item.uniqueItemId !== RegionSelection.EU_AUTHORITY
        ),
      [lang, isEidas] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const regionSelectedItem =
      regionSelectItems.find((regionItem) => {
        switch (target) {
          case UsageRegionTarget.EU_AUTHORITY:
            return regionItem.uniqueItemId === RegionSelection.EU_AUTHORITY
          case UsageRegionTarget.TARGET_COUNTRY:
            return countryKoodistoCode === EuCountries.FINLAND
              ? regionItem.uniqueItemId === RegionSelection.FINLAND
              : regionItem.uniqueItemId === RegionSelection.OTHER_COUNTRY
        }
        return false
      }) ?? null

    const { countrySelectList, countrySelectIndex } = useCountrySelectData({
      lang,
      excludeCountries: [EuCountries.FINLAND],
    })

    const showCountrySelect =
      target === UsageRegionTarget.TARGET_COUNTRY &&
      countryKoodistoCode !== EuCountries.FINLAND

    const showReceiverSelect =
      target === UsageRegionTarget.TARGET_COUNTRY &&
      isCountryInApostilleByCode(countryKoodistoCode) &&
      countryKoodistoCode !== EuCountries.FINLAND &&
      isCountryInEUByCode(countryKoodistoCode)

    const ariaOptionsFunction = useAriaOptionsFunction()

    const setUsageRegion = useMemo(() => {
      const { setUsageRegion: setUsageRegionRaw } =
        getFormActions(certificateType)
      return (region: TodistusStoreForm['usageRegion']) =>
        runInAction(() => {
          setShowValidationErrors({
            orderFlowName,
            value: false,
          })
          setUsageRegionRaw(region)
        })
    }, [certificateType, orderFlowName])

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'target',
      'country',
      'receiver',
    ] as const)

    const errors = getErrors(form)

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardUsageRegionStepMainTitle" />
          </Heading>
          <ParagraphWithNewlines data-test-id="usage-region-info">
            <LocalizedString
              stringKey={
                isEidas
                  ? 'orderWizardUsageRegionStepMainDescriptionEidas'
                  : 'orderWizardUsageRegionStepMainDescription'
              }
            />
          </ParagraphWithNewlines>
        </Block>
        <Block variant="section">
          <SingleSelect
            id="select-region-widget"
            data-test-id="single-select-region"
            labelText={t('orderWizardUsageRegionStepRegionSelectWidgetHeading')}
            visualPlaceholder={t(
              'orderWizardUsageRegionStepRegionSelectPrompt'
            )}
            items={regionSelectItems}
            clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
            noItemsText={t('orderWizardSingleSelectNoMatchingItems') ?? ''}
            ariaOptionsAvailableTextFunction={ariaOptionsFunction}
            selectedItem={regionSelectedItem}
            onItemSelectionChange={(item) => {
              const { usageRegionTarget, setCountry } = item ?? {
                usageRegionTarget: null,
                setCountry: null,
              }
              setUsageRegion({
                target: usageRegionTarget,
                countryKoodistoCode: setCountry,
                receiver: null,
              })
            }}
            status={
              showStepValidationErrors && errors.target ? 'error' : 'default'
            }
            statusText={
              showStepValidationErrors && errors.target
                ? t(
                    getPropertyTranslationKey(errors.target),
                    errors.target.errorTranslationParams
                  )
                : undefined
            }
            forwardedRef={refs.target}
          />
          {showCountrySelect && (
            <SingleSelect
              id="select-country-widget"
              data-test-id="single-select-country"
              labelText={t(
                'orderWizardUsageRegionStepCountrySelectWidgetHeading'
              )}
              visualPlaceholder={t(
                'orderWizardUsageRegionStepCountrySelectPrompt'
              )}
              items={countrySelectList}
              clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
              noItemsText={t('orderWizardSingleSelectNoMatchingItems') ?? ''}
              ariaOptionsAvailableTextFunction={ariaOptionsFunction}
              selectedItem={
                countryKoodistoCode
                  ? countrySelectIndex[countryKoodistoCode]
                  : null
              }
              onItemSelectionChange={(item) => {
                const { uniqueItemId } = item ?? { uniqueItemId: null }
                setUsageRegion({
                  target: target,
                  countryKoodistoCode: uniqueItemId,
                  receiver: receiver,
                })
              }}
              status={
                showStepValidationErrors && errors.country ? 'error' : 'default'
              }
              statusText={
                showStepValidationErrors && errors.country
                  ? t(
                      getPropertyTranslationKey(errors.country),
                      errors.country.errorTranslationParams
                    )
                  : undefined
              }
              forwardedRef={refs.country}
            />
          )}

          <DeliveryInfo
            target={target}
            countryKoodistoCode={countryKoodistoCode}
            isEidas={isEidas}
          />

          {showReceiverSelect && (
            <>
              <RadioButtonGroup
                data-test-id="radio-select-receiver-type"
                id="select-receiver-widget"
                name="receiver"
                labelText={t(
                  'orderWizardUsageRegionStepReceiverTypeSelectWidgetHeading'
                )}
                value={receiver ?? undefined}
                onChange={(value): void => {
                  setUsageRegion({
                    target,
                    countryKoodistoCode,
                    receiver: value as UsageRegionReceiver,
                  })
                }}
              >
                <RadioButton
                  value={UsageRegionReceiver.TARGET_COUNTRY_AUTHORITY}
                  forwardedRef={refs.receiver}
                >
                  <LocalizedString
                    stringKey={`orderWizardUsageRegionStepReceiverType_targetCountryAuthority`}
                  />
                </RadioButton>
                <RadioButton
                  value={UsageRegionReceiver.PRIVATE_OR_OTHER_OPERATOR}
                >
                  <LocalizedString
                    stringKey={`orderWizardUsageRegionStepReceiverType_privateOrOtherOperator`}
                  />
                </RadioButton>
              </RadioButtonGroup>{' '}
              {showStepValidationErrors && errors.receiver ? (
                <StatusText status="error">
                  {t(
                    getPropertyTranslationKey(errors.receiver),
                    errors.receiver.errorTranslationParams
                  )}
                </StatusText>
              ) : null}
            </>
          )}
        </Block>
      </FormContainer>
    )
  })
)

export default UsageRegionStep

const DeliveryInfo: React.FC<{
  target: UsageRegionTarget | null
  countryKoodistoCode: CountryKoodistoCode | null
  isEidas: boolean
}> = observer(({ target, countryKoodistoCode, isEidas }) => {
  const { t } = useTranslation()

  if (isEidas) {
    return null
  }

  const showEuAuthorityDeliveryNotify =
    target === UsageRegionTarget.EU_AUTHORITY

  const showFinlandDeliveryNotify =
    target === UsageRegionTarget.TARGET_COUNTRY &&
    countryKoodistoCode === EuCountries.FINLAND

  const showOtherCountriesDeliveryNotify =
    target === UsageRegionTarget.TARGET_COUNTRY &&
    countryKoodistoCode !== EuCountries.FINLAND

  const showLegalizeNotify =
    target === UsageRegionTarget.TARGET_COUNTRY &&
    countryKoodistoCode !== null &&
    !isCountryInApostilleByCode(countryKoodistoCode)

  return (
    <Block aria-live="polite">
      {showEuAuthorityDeliveryNotify && (
        <SmallTextParagraph data-test-id="delivery-info">
          <LocalizedString stringKey="orderWizardUsageRegionStepDeliveryNotifyEuAuthority" />
        </SmallTextParagraph>
      )}
      {showFinlandDeliveryNotify && (
        <SmallTextParagraph data-test-id="delivery-info">
          <LocalizedString stringKey="orderWizardUsageRegionStepDeliveryNotifyFinland" />
        </SmallTextParagraph>
      )}
      {showOtherCountriesDeliveryNotify && (
        <SmallTextParagraph data-test-id="delivery-info">
          <LocalizedString stringKey="orderWizardUsageRegionStepDeliveryNotifyOtherCountries" />
        </SmallTextParagraph>
      )}
      {showLegalizeNotify && (
        <InfoInlineAlert
          aria-live="polite"
          labelText={t('orderWizardUsageRegionStepDeliveryNotifyLegalizeTitle')}
          data-test-id="legalize-info"
        >
          <Paragraph>
            <LocalizedString stringKey="orderWizardUsageRegionStepDeliveryNotifyLegalizeBody" />
          </Paragraph>
          <Paragraph>
            <ExternalLink
              labelNewWindow={t('opensInNewWindow')}
              href={t(
                'orderWizardUsageRegionStepDeliveryNotifyLegalizeInstructionsLinkHref'
              )}
              underline="initial"
            >
              <LocalizedString stringKey="orderWizardUsageRegionStepDeliveryNotifyLegalizeInstructionsLinkText" />
            </ExternalLink>
          </Paragraph>
        </InfoInlineAlert>
      )}
    </Block>
  )
})

function getErrors(form: TodistusStoreForm) {
  return {
    target: getValidationErrorFromValidator(
      validateUsageRegionTarget,
      form.usageRegion.target,
      form,
      'usageRegion.target'
    ),
    country: getValidationErrorFromValidator(
      validateUsageRegionCountry,
      form.usageRegion.countryKoodistoCode,
      form,
      'usageRegion.countryKoodistoCode'
    ),
    receiver: getValidationErrorFromValidator(
      validateUsageRegionReceiver,
      form.usageRegion.receiver,
      form,
      'usageRegion.receiver'
    ),
  }
}
